import React, { useEffect, useState, useRef, useCallback } from "react";
import MaterialTable from "material-table";
import FitnessService from "../../services/FitnessService";
import { makeStyles } from "@material-ui/core/styles";
import { DateTime, Settings } from "luxon";
import {
    Chip,
    Typography,
    Box,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Divider,
} from "@material-ui/core";
import AssessmentIcon from "@material-ui/icons/Assessment";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CloseIcon from "@material-ui/icons/Close";

Settings.defaultZone = "Europe/Sofia";

const REFRESH_INTERVAL = 10; // seconds

const useStyles = makeStyles((theme) => ({
    toolbar: {
        marginLeft: theme.spacing(2),
    },
    headerBox: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(2),
        backgroundColor: "#f5f5f5",
        borderBottom: "1px solid #e0e0e0",
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
    },
    headerIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    refreshContainer: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    countdownBox: {
        display: "flex",
        alignItems: "center",
        backgroundColor: "#e3f2fd",
        borderRadius: theme.spacing(1),
        padding: theme.spacing(0.5, 1.5),
        minWidth: 180,
    },
    countdownText: {
        fontSize: "0.85rem",
        color: theme.palette.primary.main,
        marginRight: theme.spacing(1),
    },
    countdownNumber: {
        fontWeight: "bold",
        fontSize: "1rem",
        color: theme.palette.primary.dark,
        minWidth: 24,
        textAlign: "center",
    },
    progressBar: {
        width: 60,
        marginLeft: theme.spacing(1),
        borderRadius: 4,
    },
    refreshIcon: {
        animation: "$spin 1s linear infinite",
    },
    "@keyframes spin": {
        "0%": {
            transform: "rotate(0deg)",
        },
        "100%": {
            transform: "rotate(360deg)",
        },
    },
    entryCountChip: {
        fontWeight: "bold",
    },
    dialogTitle: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: theme.palette.primary.main,
        color: "white",
    },
    dialogContent: {
        padding: theme.spacing(2),
        minWidth: 400,
    },
    entryListItem: {
        borderBottom: "1px solid #f0f0f0",
    },
    guestInfo: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: "#f9f9f9",
        borderRadius: theme.spacing(1),
    },
}));

const FitnessReportTable = () => {
    const classes = useStyles();
    const tableRef = useRef(null);
    const refreshIntervalRef = useRef(null);
    const countdownIntervalRef = useRef(null);

    const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // State for entry details dialog
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedGuest, setSelectedGuest] = useState(null);
    const [guestEntries, setGuestEntries] = useState([]);
    const [loadingEntries, setLoadingEntries] = useState(false);

    const [iserror, setIserror] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    // Function to refresh table data
    const refreshTableData = useCallback(() => {
        if (tableRef.current) {
            setIsRefreshing(true);
            tableRef.current.onQueryChange();
            setTimeout(() => setIsRefreshing(false), 500);
        }
        setCountdown(REFRESH_INTERVAL);
    }, []);

    // Function to refresh modal data
    const refreshModalData = useCallback(async () => {
        if (!selectedGuest) return;
        setIsRefreshing(true);
        try {
            const response = await FitnessService.getReport(0, 1000, selectedGuest.guest_name, "time", "DESC");
            const userEntries = response.data.data.filter(
                (entry) => entry.user_id === selectedGuest.user_id
            );
            setGuestEntries(userEntries);
        } catch (error) {
            console.error("Error refreshing guest entries:", error);
        } finally {
            setTimeout(() => setIsRefreshing(false), 500);
        }
        setCountdown(REFRESH_INTERVAL);
    }, [selectedGuest]);

    // Manual refresh handler
    const handleManualRefresh = useCallback(() => {
        if (detailsOpen) {
            refreshModalData();
        } else {
            refreshTableData();
        }
    }, [detailsOpen, refreshModalData, refreshTableData]);

    // Setup auto-refresh with countdown — switches target based on modal state
    useEffect(() => {
        countdownIntervalRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    return REFRESH_INTERVAL;
                }
                return prev - 1;
            });
        }, 1000);

        refreshIntervalRef.current = setInterval(() => {
            if (detailsOpen) {
                refreshModalData();
            } else {
                refreshTableData();
            }
        }, REFRESH_INTERVAL * 1000);

        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
        };
    }, [detailsOpen, refreshModalData, refreshTableData]);

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        try {
            return DateTime.fromISO(dateString)
                .setZone("Europe/Sofia")
                .toFormat("dd.MM.yyyy");
        } catch (e) {
            return dateString;
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "-";
        try {
            return DateTime.fromISO(dateString)
                .setZone("Europe/Sofia")
                .toFormat("dd.MM.yyyy HH:mm");
        } catch (e) {
            return dateString;
        }
    };

    // Handle viewing entry details for a guest
    const handleViewEntries = async (guest) => {
        setSelectedGuest(guest);
        setDetailsOpen(true);
        setLoadingEntries(true);
        setCountdown(REFRESH_INTERVAL);

        try {
            const response = await FitnessService.getReport(0, 1000, guest.guest_name, "time", "DESC");
            const userEntries = response.data.data.filter(
                (entry) => entry.user_id === guest.user_id
            );
            setGuestEntries(userEntries);
        } catch (error) {
            console.error("Error fetching guest entries:", error);
            setGuestEntries([]);
        } finally {
            setLoadingEntries(false);
        }
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedGuest(null);
        setGuestEntries([]);
        setCountdown(REFRESH_INTERVAL);
    };

    const [columns] = useState([
        {
            title: "Име на Гост",
            field: "guest_name",
            editable: "never",
            cellStyle: (e, rowData) => {
                if (rowData.guest_name === "null" || rowData.guest_name === "empty") {
                    return { backgroundColor: "#FFCCCC" };
                }
            },
            emptyValue: () => "Неизвестен",
        },
        {
            title: "Стая",
            field: "room_name",
            editable: "never",
            render: (rowData) => rowData.room_name || "-",
            cellStyle: {
                textAlign: "center",
            },
            headerStyle: {
                textAlign: "center",
            },
        },
        {
            title: "Резервация От:",
            field: "start_date",
            editable: "never",
            render: (rowData) => formatDate(rowData.start_date),
            cellStyle: {
                textAlign: "center",
            },
            headerStyle: {
                textAlign: "center",
            },
        },
        {
            title: "Резервация До:",
            field: "end_date",
            editable: "never",
            render: (rowData) => formatDate(rowData.end_date),
            cellStyle: {
                textAlign: "center",
            },
            headerStyle: {
                textAlign: "center",
            },
        },
        {
            title: "Последно посещение",
            field: "last_entry",
            editable: "never",
            render: (rowData) => formatDateTime(rowData.last_entry),
            cellStyle: {
                textAlign: "center",
            },
            headerStyle: {
                textAlign: "center",
            },
        },
        {
            title: "Брой посещения",
            field: "entry_count",
            editable: "never",
            type: "numeric",
            render: (rowData) => (
                <Chip
                    label={rowData.entry_count || 0}
                    color="primary"
                    size="small"
                    className={classes.entryCountChip}
                />
            ),
            cellStyle: {
                textAlign: "center",
            },
            headerStyle: {
                textAlign: "center",
            },
        },
        {
            title: "ID",
            field: "user_id",
            editable: "never",
            cellStyle: { width: "8%" },
            width: "8%",
            headerStyle: { width: "8%" },
            emptyValue: () => "empty",
        },
    ]);

    // Calculate progress percentage
    const progressValue = (countdown / REFRESH_INTERVAL) * 100;

    return (
        <div>
            <Box className={classes.headerBox}>
                <Box className={classes.headerLeft}>
                    <AssessmentIcon className={classes.headerIcon} fontSize="large" />
                    <Typography variant="h6">Фитнес Справка - Пълна информация за посещения</Typography>
                </Box>
                <Box className={classes.refreshContainer}>
                    <Box className={classes.countdownBox}>
                        {isRefreshing ? (
                            <RefreshIcon className={classes.refreshIcon} color="primary" />
                        ) : (
                            <>
                                <Typography className={classes.countdownText}>
                                    Обновяване след:
                                </Typography>
                                <Typography className={classes.countdownNumber}>
                                    {countdown}сек.
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={progressValue}
                                    className={classes.progressBar}
                                />
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
            <MaterialTable
                title=""
                columns={columns}
                tableRef={tableRef}
                data={(query) =>
                    new Promise((resolve, reject) => {
                        // Use the summary endpoint which groups by user
                        FitnessService.getSummary(
                            query.page,
                            query.pageSize,
                            query.search
                        )
                            .then((response) => {
                                resolve({
                                    data: response.data.data,
                                    page: response.data.page,
                                    totalCount: response.data.totalCount,
                                });
                            })
                            .catch((error) => {
                                console.error("Error fetching fitness report:", error);
                                setErrorMessages(["Грешка при зареждане на данните"]);
                                setIserror(true);
                                reject(error);
                            });
                    })
                }
                style={{
                    borderRadius: "0",
                    boxShadow: "none",
                }}
                actions={[
                    {
                        icon: "refresh",
                        tooltip: "Обнови сега",
                        isFreeAction: true,
                        onClick: handleManualRefresh,
                    },
                    {
                        icon: () => <VisibilityIcon />,
                        tooltip: "Виж всички посещения",
                        onClick: (event, rowData) => handleViewEntries(rowData),
                    },
                ]}
                options={{
                    loadingType: "linear",
                    pageSize: 50,
                    maxBodyHeight: "68vh",
                    showTitle: false,
                    sorting: true,
                    actionsColumnIndex: -1,
                    exportButton: { csv: true, pdf: false },
                    filtering: false,
                    pageSizeOptions: [50, 100, 200],
                    search: true,
                    toolbar: true,
                    debounceInterval: 500,
                    searchFieldAlignment: "right",
                    searchFieldStyle: { width: "300px" },
                }}
                localization={{
                    pagination: {
                        labelDisplayedRows: "{from}-{to} от {count}",
                        labelRowsSelect: "Гости",
                    },
                    header: {
                        actions: "Действия",
                    },
                    body: {
                        emptyDataSourceMessage: "Няма записи за фитнес посещения",
                        filterRow: {
                            filterTooltip: "Филтър",
                        },
                    },
                    toolbar: {
                        exportTitle: "Експорт",
                        exportName: "Експорт към CSV",
                        searchTooltip: "Търси",
                        searchPlaceholder: "Търси по име, стая...",
                    },
                }}
            />

            {/* Entry Details Dialog */}
            <Dialog
                open={detailsOpen}
                onClose={handleCloseDetails}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle disableTypography className={classes.dialogTitle}>
                    <Typography variant="h6">
                        Всички посещения
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDetails}
                        style={{ color: "white" }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {selectedGuest && (
                        <Box className={classes.guestInfo}>
                            <Typography variant="subtitle1">
                                <strong>Гост:</strong> {selectedGuest.guest_name}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Стая:</strong> {selectedGuest.room_name || "-"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Период:</strong> {formatDate(selectedGuest.start_date)} - {formatDate(selectedGuest.end_date)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Общо посещения:</strong> {selectedGuest.entry_count}
                            </Typography>
                        </Box>
                    )}
                    <Divider />
                    <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                        История на посещенията:
                    </Typography>
                    {loadingEntries ? (
                        <Typography>Зареждане...</Typography>
                    ) : guestEntries.length > 0 ? (
                        <List dense>
                            {guestEntries.map((entry, index) => (
                                <ListItem key={entry.id || index} className={classes.entryListItem}>
                                    <ListItemIcon>
                                        <AccessTimeIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={formatDateTime(entry.time)}
                                        secondary={entry.event === "enter" ? "Влизане" : entry.event === "exit" ? "Излизане" : entry.event}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography color="textSecondary">
                            Няма записани посещения
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetails} color="primary">
                        Затвори
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default FitnessReportTable;
